import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import Notification from "./Notification";
import {
  DeleteAppointmentSlot,
  fetchSlotConfirmationSummaryByListing,
  fetchAppointmentsPerListingData,
  saveAppointments,
  saveMainAppointment,
} from "./DataServices/AppointmentsDataServices";
import type { IAvailableAppointment, Reminder } from "./Models";
import type { IListing } from "./RentedListing";

const localizer = momentLocalizer(moment);

interface IPlannerProps {
  listings: IListing[];
  onBack: () => void;
}

interface IPlannedSlot {
  id: string;
  cumulativeGroupId?: string;
  date: Date;
  startTime: string;
  durationMinutes: number;
  capacity: number;
  slotOrder: number;
  assignedListingIds: string[];
}

interface ICalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  source?: "existing" | "planned";
  appointmentId?: string;
  capacity?: number;
  listingId?: string;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function combineDateAndTime(date: Date, timeHHmm: string) {
  const [h, m] = timeHHmm.split(":").map((v) => Number(v));
  const d = new Date(date);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

function getDatesInRange(start: Date, end: Date) {
  const dates: Date[] = [];
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);

  const last = new Date(end);
  last.setHours(0, 0, 0, 0);

  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export default function ArrangeShowingsPlanner({
  listings,
  onBack,
}: IPlannerProps) {
  const cancelButtonRef = useRef(null);
  const deleteCancelButtonRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [startTime, setStartTime] = useState("09:00");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [capacity, setCapacity] = useState(1);
  const [selectedStartTimeOnly, setSelectedStartTimeOnly] = useState("09:00");
  const [selectedEndTimeOnly, setSelectedEndTimeOnly] = useState("10:00");
  const [isConfigureByDate, setIsConfigureByDate] = useState(true);
  const [marketingOpeningTime, setMarketingOpeningTime] =
    useState<dayjs.Dayjs | null>(null);
  const [marketingClosingTime, setMarketingClosingTime] =
    useState<dayjs.Dayjs | null>(null);
  const [openHoursBefore, setOpenHoursBefore] = useState<number | undefined>(
    undefined,
  );
  const [openHoursAfter, setOpenHoursAfter] = useState<number | undefined>(
    undefined,
  );
  const [resendUnconfirmed, setResendUnconfirmed] = useState(false);
  const [resendCancelled, setResendCancelled] = useState(false);
  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);
  const [plannedSlots, setPlannedSlots] = useState<IPlannedSlot[]>([]);
  const [isCreateSlotModalOpen, setIsCreateSlotModalOpen] = useState(false);
  const [existingEvents, setExistingEvents] = useState<ICalendarEvent[]>([]);
  const [existingDateKeys, setExistingDateKeys] = useState<Set<string>>(
    new Set(),
  );
  const [slotConfirmedCountByAppointmentId, setSlotConfirmedCountByAppointmentId] =
    useState<Record<string, number>>({});
  const [tenantCountByListingId, setTenantCountByListingId] = useState<Record<string, number>>({});
  const [isDeleteSlotModalOpen, setIsDeleteSlotModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    mode: "planned" | "existing";
    id: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [days, setDays] = useState<number | undefined>(undefined);
  const [hours, setHours] = useState<number | undefined>(undefined);
  const [minutes, setMinutes] = useState<number | undefined>(undefined);
  const [relativeReminderMinutes, setRelativeReminderMinutes] = useState<
    number[]
  >([48 * 60]);

  useEffect(() => {
    const loadExistingAppointments = async () => {
      try {
        const responses = await Promise.all(
          listings.map((listing) =>
            fetchAppointmentsPerListingData(listing.id),
          ),
        );

        const flattened = responses.flatMap((r) => r ?? []);
        const map = new Map<string, ICalendarEvent>();
        const existingKeys = new Set<string>();

        for (const appointment of flattened) {
          if (
            !appointment?.id ||
            !appointment?.startTime ||
            !appointment?.endTime
          ) {
            continue;
          }

          const listing = listings.find((l) => l.id === appointment.listingID);
          const startDate = new Date(appointment.startTime);
          existingKeys.add(dateKey(startDate));

          map.set(appointment.id, {
            id: `existing::${appointment.id}`,
            title: listing?.title ?? "Apartment",
            start: startDate,
            end: new Date(appointment.endTime),
            source: "existing",
            appointmentId: appointment.id,
            capacity: Number(appointment.capacity ?? 0),
            listingId: appointment.listingID,
          });
        }

        setExistingEvents(Array.from(map.values()));
        setExistingDateKeys(existingKeys);
      } catch {
        setExistingEvents([]);
        setExistingDateKeys(new Set());
      }
    };

    if (listings.length > 0) {
      loadExistingAppointments();
    }
  }, [listings]);

  useEffect(() => {
    const loadSlotConfirmationSummary = async () => {
      try {
        const responses = await Promise.all(
          listings.map((listing) =>
            fetchSlotConfirmationSummaryByListing(listing.id),
          ),
        );

        const counts: Record<string, number> = {};
        const tenantCounts: Record<string, number> = {};

        responses.forEach((summary: any, index) => {
          if (!summary) return;

          const listingId = String(
            summary.listingId ?? summary.ListingId ?? listings[index]?.id ?? "",
          );

          const totalTenants = Number(summary.totalTenants ?? summary.TotalTenants ?? 0);
          if (listingId) {
            tenantCounts[listingId] = totalTenants;
          }

          const slotCounts = (summary.slotCounts ?? summary.SlotCounts ?? []) as any[];
          slotCounts.forEach((slot) => {
            const slotId = String(slot.slotId ?? slot.SlotId ?? "");
            const confirmedCount = Number(slot.confirmedCount ?? slot.ConfirmedCount ?? 0);
            if (slotId) {
              counts[slotId] = confirmedCount;
            }
          });
        });

        setSlotConfirmedCountByAppointmentId(counts);
        setTenantCountByListingId(tenantCounts);
      } catch {
        setSlotConfirmedCountByAppointmentId({});
        setTenantCountByListingId({});
      }
    };

    if (listings.length > 0) {
      loadSlotConfirmationSummary();
    }
  }, [listings]);

  const selectedDateKeySet = useMemo(() => {
    return new Set(selectedDates.map((d) => dateKey(d)));
  }, [selectedDates]);

  const existingDateKeySet = useMemo(() => {
    return new Set(existingEvents.map((e) => dateKey(e.start)));
  }, [existingEvents]);

  const plannedEvents: ICalendarEvent[] = useMemo(() => {
    return plannedSlots.flatMap((slot) => {
      const start = combineDateAndTime(slot.date, slot.startTime);
      const end = new Date(start.getTime() + slot.durationMinutes * 60 * 1000);

      if (slot.assignedListingIds.length === 0) {
        return [
          {
            id: `planned::${slot.id}::unassigned`,
            title: `Unassigned Slot`,
            start,
            end,
            source: "planned",
          },
        ];
      }

      return slot.assignedListingIds.map((listingId) => {
        const listing = listings.find((l) => l.id === listingId);
        return {
          id: `planned::${slot.id}::${listingId}`,
          title: listing ? listing.title : "Apartment",
          start,
          end,
          source: "planned",
        };
      });
    });
  }, [plannedSlots, listings]);

  const events: ICalendarEvent[] = useMemo(() => {
    return [...existingEvents, ...plannedEvents];
  }, [existingEvents, plannedEvents]);

  const toggleListingSelection = (listingId: string) => {
    setSelectedListingIds((prev) => {
      if (prev.includes(listingId)) {
        return prev.filter((id) => id !== listingId);
      }
      return [...prev, listingId];
    });
  };

  const createSlots = () => {
    const normalizedDates =
      selectedDates.length > 0
        ? selectedDates
        : selectedDate
          ? [selectedDate]
          : [];
    const effectiveDuration = Math.max(5, durationMinutes);

    const [startHour, startMinute] = selectedStartTimeOnly
      .split(":")
      .map(Number);
    const [endHour, endMinute] = selectedEndTimeOnly.split(":").map(Number);

    if (
      Number.isNaN(startHour) ||
      Number.isNaN(startMinute) ||
      Number.isNaN(endHour) ||
      Number.isNaN(endMinute)
    ) {
      setNotificationMessage("Please select valid start and end time.");
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    if (normalizedDates.length === 0) {
      setNotificationMessage("Please select date(s) on calendar first.");
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    if (selectedListingIds.length === 0) {
      setNotificationMessage("Please multi-select at least one apartment.");
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    const cumulativeGroupId =
      normalizedDates.length > 1 ? crypto.randomUUID() : undefined;

    const slotsToAdd: IPlannedSlot[] = [];

    for (const date of normalizedDates) {
      const existingCountForDay = plannedSlots.filter((s) =>
        isSameDay(s.date, date),
      ).length;

      const rangeStart = new Date(date);
      rangeStart.setHours(startHour, startMinute, 0, 0);

      const rangeEnd = new Date(date);
      rangeEnd.setHours(endHour, endMinute, 0, 0);

      if (rangeEnd <= rangeStart) {
        setNotificationMessage("End time must be after start time.");
        setNotificationType("error");
        setOpenNotification(true);
        return;
      }

      let cursor = new Date(rangeStart);
      let orderOffset = 0;

      while (cursor < rangeEnd) {
        const next = new Date(cursor.getTime() + effectiveDuration * 60 * 1000);
        if (next > rangeEnd) break;

        const hh = String(cursor.getHours()).padStart(2, "0");
        const mm = String(cursor.getMinutes()).padStart(2, "0");

        slotsToAdd.push({
          id: crypto.randomUUID(),
          cumulativeGroupId,
          date: new Date(date),
          startTime: `${hh}:${mm}`,
          durationMinutes: effectiveDuration,
          capacity,
          slotOrder: existingCountForDay + orderOffset + 1,
          assignedListingIds: [...selectedListingIds],
        });

        orderOffset++;
        cursor = next;
      }
    }

    if (slotsToAdd.length === 0) {
      setNotificationMessage(
        "No slot could be created for the selected range and duration.",
      );
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    setPlannedSlots((prev) => [...prev, ...slotsToAdd]);
    setIsCreateSlotModalOpen(false);
    setNotificationMessage(
      `Created ${slotsToAdd.length} slot(s) successfully.`,
    );
    setNotificationType("success");
    setOpenNotification(true);
  };

  const toggleSelectedDate = (date: Date) => {
    const key = dateKey(date);
    setSelectedDates((prev) => {
      const exists = prev.some((d) => dateKey(d) === key);
      if (exists) {
        return prev.filter((d) => dateKey(d) !== key);
      }
      return [...prev, new Date(date)];
    });
    setSelectedDate(new Date(date));
  };

  const deleteSlot = (slotId: string) => {
    setPlannedSlots((prev) => prev.filter((slot) => slot.id !== slotId));
  };

  const closeDeleteSlotModal = () => {
    setIsDeleteSlotModalOpen(false);
    setPendingDelete(null);
  };

  const openDeleteSlotModalFromEvent = (event: ICalendarEvent) => {
    if (typeof event.id !== "string") return;

    if (event.id.startsWith("planned::")) {
      const parts = event.id.split("::");
      const plannedSlotId = parts[1];
      if (!plannedSlotId) return;
      setPendingDelete({ mode: "planned", id: plannedSlotId });
      setIsDeleteSlotModalOpen(true);
      return;
    }

    if (event.source === "existing" && event.appointmentId) {
      setPendingDelete({ mode: "existing", id: event.appointmentId });
      setIsDeleteSlotModalOpen(true);
    }
  };

  const confirmDeleteSlot = async () => {
    if (!pendingDelete) return;

    try {
      if (pendingDelete.mode === "planned") {
        deleteSlot(pendingDelete.id);
      } else {
        await DeleteAppointmentSlot(pendingDelete.id);
        setExistingEvents((prev) => {
          const next = prev.filter((e) => e.appointmentId !== pendingDelete.id);
          setExistingDateKeys(new Set(next.map((e) => dateKey(e.start))));
          return next;
        });
      }

      setNotificationMessage("Slot deleted successfully.");
      setNotificationType("success");
      setOpenNotification(true);
    } catch {
      setNotificationMessage("Failed to delete slot.");
      setNotificationType("error");
      setOpenNotification(true);
    } finally {
      closeDeleteSlotModal();
    }
  };

  const getSlotDateLabels = (slot: IPlannedSlot) => {
    const relatedSlots = plannedSlots.filter((s) => {
      if (slot.cumulativeGroupId) {
        return s.cumulativeGroupId === slot.cumulativeGroupId;
      }
      return s.id === slot.id;
    });

    const labels = Array.from(new Set(relatedSlots.map((s) => dateKey(s.date))))
      .map((k) => {
        const [y, m, d] = k.split("-").map(Number);
        return new Date(y, m, d).toLocaleDateString();
      })
      .join(", ");

    return labels;
  };

  const getSundayBeforeReminderMinutes = (showingStart: Date) => {
    const sundayBefore = new Date(showingStart);
    sundayBefore.setHours(0, 0, 0, 0);
    sundayBefore.setDate(sundayBefore.getDate() - sundayBefore.getDay());

    if (sundayBefore >= showingStart) {
      sundayBefore.setDate(sundayBefore.getDate() - 7);
    }

    const diffMinutes = Math.floor(
      (showingStart.getTime() - sundayBefore.getTime()) / (1000 * 60),
    );
    return Math.max(diffMinutes, 1);
  };

  const displayReminder = (minutesTotal: number) => {
    const day = Math.floor(minutesTotal / (24 * 60));
    const leftAfterDay = minutesTotal % (24 * 60);
    const hour = Math.floor(leftAfterDay / 60);
    const minute = leftAfterDay % 60;
    return `${day} Days ${hour} Hrs ${minute} Mins`;
  };

  const openCreateSlotsModal = () => {
    const [h, m] = startTime.split(":").map((v) => Number(v));
    const startHour = h || 0;
    const startMinute = m || 0;

    const start = new Date();
    start.setHours(startHour, startMinute, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    const startTimeText = `${String(startHour).padStart(2, "0")}:${String(startMinute).padStart(2, "0")}`;
    const endTimeText = `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;

    setSelectedStartTimeOnly(startTimeText);
    setSelectedEndTimeOnly(endTimeText);
    setMarketingOpeningTime(dayjs(start));
    setMarketingClosingTime(dayjs(end));
    setDurationMinutes(60);
    setIsCreateSlotModalOpen(true);
  };

  const getSelectedDatesText = () => {
    const datesToShow =
      selectedDates.length > 0
        ? selectedDates
        : selectedDate
          ? [selectedDate]
          : [];
    if (datesToShow.length === 0) return "No date selected";

    const unique = Array.from(new Set(datesToShow.map((d) => dateKey(d))));
    return unique
      .map((k) => {
        const [y, m, d] = k.split("-").map(Number);
        return new Date(y, m, d).toLocaleDateString();
      })
      .join(", ");
  };

  const handleAddReminder = () => {
    if (!days && !hours && !minutes) {
      setNotificationMessage("Please enter reminder time.");
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    const dd = days ?? 0;
    const hh = hours ?? 0;
    const mm = minutes ?? 0;

    if (hh >= 24 || mm >= 60) {
      setNotificationMessage(
        "Invalid reminder. Hours must be < 24 and minutes < 60.",
      );
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    const total = dd * 24 * 60 + hh * 60 + mm;
    if (total <= 0) {
      setNotificationMessage("Reminder must be greater than 0.");
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    setRelativeReminderMinutes((prev) =>
      prev.includes(total) ? prev : [...prev, total].sort((a, b) => b - a),
    );
    setDays(undefined);
    setHours(undefined);
    setMinutes(undefined);
  };

  const handleDeleteReminder = (value: number) => {
    setRelativeReminderMinutes((prev) => prev.filter((r) => r !== value));
  };

  const savePlannedShowings = async () => {
    const appointments: IAvailableAppointment[] = plannedSlots.flatMap(
      (slot) => {
        const start = combineDateAndTime(slot.date, slot.startTime);
        const end = new Date(
          start.getTime() + slot.durationMinutes * 60 * 1000,
        );
        const sundayReminderMinutes = getSundayBeforeReminderMinutes(start);

        const defaultReminders: Reminder[] = [
          {
            id: "",
            availableAppointmentId: "",
            reminder: sundayReminderMinutes,
            reminderType: "Reminder",
          },
          ...relativeReminderMinutes.map((r) => ({
            id: "",
            availableAppointmentId: "",
            reminder: r,
            reminderType: "Reminder",
          })),
        ];

        return slot.assignedListingIds.map((listingId) => ({
          id: crypto.randomUUID(),
          listingID: listingId,
          startTime: new Date(start),
          endTime: new Date(end),
          capacity: slot.capacity,
          slotOrder: slot.slotOrder,
          groupId: slot.id,
          reminders: defaultReminders,
        }));
      },
    );

    if (appointments.length === 0) {
      setNotificationMessage(
        "Assign at least one apartment to a slot before saving.",
      );
      setNotificationType("error");
      setOpenNotification(true);
      return;
    }

    try {
      setIsSaving(true);
      // Ensure there's a MainAppointment (group) per listing so backend consumers can find it
      const byListing = new Map<string, IAvailableAppointment[]>();
      for (const a of appointments) {
        const list = byListing.get(a.listingID) ?? [];
        list.push(a);
        byListing.set(a.listingID, list);
      }

      const listingToMainId = new Map<string, string>();

      for (const [listingId, appts] of Array.from(byListing.entries()) as [
        string,
        IAvailableAppointment[],
      ][]) {
        // compute earliest start and latest end for this listing
        const timedAppts = appts.filter(
          (
            x,
          ): x is IAvailableAppointment & { startTime: Date; endTime: Date } =>
            x.startTime !== null && x.endTime !== null,
        );
        const starts = timedAppts.map((x) => new Date(x.startTime).getTime());
        const ends = timedAppts.map((x) => new Date(x.endTime).getTime());
        const openingTime = new Date(Math.min(...starts));
        const closingTime = new Date(Math.max(...ends));

        const mainId = crypto.randomUUID();
        const mainAppointment = {
          id: mainId,
          listingId: listingId,
          openingTime: isConfigureByDate ? openingTime : null,
          closingTime: isConfigureByDate ? closingTime : null,
          // Match AddAppointment configured-by-date defaults (0 hours) when configured by date
          openHoursBefore: isConfigureByDate
            ? (openHoursBefore ?? 0)
            : (openHoursBefore ?? 48),
          openHoursAfter: openHoursAfter ?? 0,
          rosterTime: undefined,
          resendCancelled: resendCancelled,
          resendUnconfirmed: resendUnconfirmed,
        };

        try {
          await saveMainAppointment(mainAppointment as any);
          listingToMainId.set(listingId, mainId);
        } catch (e) {
          // fallback to using the slot-based group id if main save fails
          console.error(
            "Failed to save main appointment for listing",
            listingId,
            e,
          );
        }
      }

      // replace groupId on appointments with main appointment id when available
      for (const a of appointments) {
        const m = listingToMainId.get(a.listingID);
        if (m) a.groupId = m;
      }

      // Save appointments per-listing to ensure backend computes slotOrder correctly
      // Backend SaveAvailableAppointmentsForListing uses position counter, which only works
      // correctly when called per-listing. This matches AddAppointment's behavior.
      for (const [listingId, appts] of Array.from(byListing.entries()) as [
        string,
        IAvailableAppointment[],
      ][]) {
        // Ensure slotOrder is 0; backend will compute via DefaultSlotOrder + position
        const appointmentsForListing = appts.map((a) => ({
          ...a,
          slotOrder: 0,
        }));

        // Save appointments for this listing (creates + attaches reminders via AddReminder)
        await saveAppointments(appointmentsForListing);
      }

      setExistingDateKeys((prev) => {
        const next = new Set(prev);
        plannedSlots.forEach((slot) => next.add(dateKey(slot.date)));
        return next;
      });
      setSelectedDates([]);
      setSelectedDate(null);

      setNotificationMessage("Showings arranged successfully.");
      setNotificationType("success");
      setOpenNotification(true);
    } catch (error: any) {
      setNotificationMessage(
        error?.message || "Failed to save arranged showings.",
      );
      setNotificationType("error");
      setOpenNotification(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200/70 bg-gradient-to-b from-slate-50 to-white p-3 sm:p-4">
      <style>{`
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white/90 px-3 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back
          </button>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            Arrange Showings
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCreateSlotsModal}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100"
          >
            <CalendarDaysIcon className="h-4 w-4 mr-1" />
            Create Slots
          </button>
          <button
            type="button"
            onClick={savePlannedShowings}
            disabled={isSaving}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save Arranged Showings"}
          </button>
        </div>
      </div>

      <Notification
        isOpen={openNotification}
        message={notificationMessage}
        messageType={notificationType}
        setIsOpen={setOpenNotification}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.5fr)_minmax(0,9.5fr)] gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            Apartments (multi-select)
          </h3>
          <div className="no-scrollbar max-h-[74vh] overflow-y-auto overflow-x-hidden pr-1 space-y-2">
            {listings.length === 0 ? (
              <div className="text-xs text-gray-500 rounded-md border border-dashed border-gray-300 px-3 py-3 text-center">
                No listed apartments found.
              </div>
            ) : (
              listings.map((listing) => (
                <div
                  key={listing.id}
                  onClick={() => toggleListingSelection(listing.id)}
                  className={`group cursor-pointer rounded-lg border px-3 py-2 text-sm transition-all ${
                    selectedListingIds.includes(listing.id)
                      ? "border-indigo-500 bg-indigo-50 text-indigo-900 shadow-sm"
                      : "border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300 hover:bg-gray-100"
                  }`}
                  title="Click to select or unselect apartment"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedListingIds.includes(listing.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleListingSelection(listing.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>{listing.title}</span>
                  </label>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Calendar</h3>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700">
                <span className="h-2 w-2 rounded-full bg-blue-400" /> Selected date
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
                <span className="h-2 w-2 rounded-full bg-amber-300" /> Existing slots
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-300" /> Fully confirmed
              </span>
            </div>
          </div>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            dayPropGetter={(date) => {
              const key = dateKey(date);
              const isSelectedDay = selectedDateKeySet.has(key);
              const hasExistingSlots = existingDateKeys.has(key);

              if (!isSelectedDay && !hasExistingSlots) return {};

              return {
                style: {
                  backgroundColor: isSelectedDay
                    ? "#DBEAFE"
                    : hasExistingSlots
                      ? "#FEFCE8"
                      : undefined,
                  border: isSelectedDay
                    ? "1px solid #93C5FD"
                    : hasExistingSlots
                      ? "1px solid #FDE68A"
                      : undefined,
                  borderRadius: "10px",
                },
              };
            }}
            selectable
            onSelectSlot={(slotInfo) => {
              const normalizedEnd = new Date(slotInfo.end);
              if (
                normalizedEnd > slotInfo.start &&
                normalizedEnd.getHours() === 0 &&
                normalizedEnd.getMinutes() === 0
              ) {
                normalizedEnd.setDate(normalizedEnd.getDate() - 1);
              }

              const rangeDates = getDatesInRange(slotInfo.start, normalizedEnd);
              setSelectedDates((prev) => {
                // If user clicks a single already-selected day, unselect it.
                if (rangeDates.length === 1) {
                  const key = dateKey(rangeDates[0]);
                  const alreadySelected = prev.some((d) => dateKey(d) === key);
                  if (alreadySelected) {
                    return prev.filter((d) => dateKey(d) !== key);
                  }
                }

                const map = new Map(prev.map((d) => [dateKey(d), d]));
                for (const d of rangeDates) {
                  map.set(dateKey(d), d);
                }
                return Array.from(map.values());
              });
              setSelectedDate(slotInfo.start);
            }}
            onSelectEvent={(event) => {
              toggleSelectedDate(event.start);
            }}
            onDoubleClickEvent={(event) => {
              openDeleteSlotModalFromEvent(event as ICalendarEvent);
            }}
            style={{ height: "70vh" }}
            eventPropGetter={(event) => {
              const e = event as ICalendarEvent;
              const confirmedCount = e.appointmentId
                ? slotConfirmedCountByAppointmentId[e.appointmentId] ?? 0
                : 0;
              const listingTenantCount = e.listingId
                ? tenantCountByListingId[e.listingId] ?? 0
                : 0;
              const isFullyConfirmed =
                e.source === "existing" &&
                listingTenantCount > 0 &&
                confirmedCount >= listingTenantCount;

              return {
                style: {
                  backgroundColor:
                    e.source === "existing"
                      ? isFullyConfirmed
                        ? "#86EFAC"
                        : "#FEF3C7"
                      : "#4f46e5",
                  borderRadius: "6px",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
                  color:
                    e.source === "existing"
                      ? isFullyConfirmed
                        ? "#064E3B"
                        : "#78350F"
                      : "#fff",
                  border: "none",
                },
              };
            }}
          />
        </div>
      </div>

      <Transition.Root show={isCreateSlotModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={setIsCreateSlotModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/70 backdrop-blur-[1px] transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden w-[92%] max-w-[1320px] rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:p-6">
                  <div className="flex items-center justify-center gap-2 mt-2 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon
                        className="h-6 w-6 text-green-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Schedule Appointment
                      </Dialog.Title>
                    </div>
                  </div>

                  <div className="flex justify-center overflow-hidden rounded-xl border border-slate-200 px-1">
                    <div className="w-1/4 border-r bg-slate-50/60 pt-3 text-center sm:pt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Schedule Appointment
                      </Dialog.Title>

                      <div className="flex flex-wrap items-center justify-center my-4">
                        <div className="text-start md:max-w-[50%] px-3 mb-6 md:mb-0">
                          <label
                            className="text-start text-gray-700 text-sm font-medium mb-2"
                            htmlFor="slotStartTime"
                          >
                            Start Time
                          </label>
                          <input
                            className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type="time"
                            id="slotStartTime"
                            value={selectedStartTimeOnly}
                            onChange={(e) =>
                              setSelectedStartTimeOnly(e.target.value)
                            }
                          />
                        </div>

                        <div className="text-start md:max-w-[50%] px-3 mb-6 md:mb-0">
                          <label
                            className="text-start text-gray-700 text-sm font-medium mb-2"
                            htmlFor="slotEndTime"
                          >
                            End Time
                          </label>
                          <input
                            className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type="time"
                            id="slotEndTime"
                            value={selectedEndTimeOnly}
                            onChange={(e) =>
                              setSelectedEndTimeOnly(e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-center my-4">
                        <div className="text-start md:max-w-[50%] px-3 mb-6 md:mb-0">
                          <label
                            className="text-start text-gray-700 text-sm font-medium mb-2"
                            htmlFor="slotDuration"
                          >
                            Slot Duration
                          </label>
                          <input
                            className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type="number"
                            id="slotDuration"
                            min={5}
                            step={5}
                            value={durationMinutes}
                            onChange={(e) =>
                              setDurationMinutes(
                                Math.max(5, Number(e.target.value) || 5),
                              )
                            }
                          />
                        </div>

                        <div className="text-start md:md:max-w-[50%] px-3 mb-6 md:mb-0">
                          <label
                            className="text-gray-700 text-sm font-medium mb-2"
                            htmlFor="capacity"
                          >
                            Slot Capacity
                          </label>
                          <input
                            className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type="number"
                            id="capacity"
                            name="capacity"
                            min={1}
                            value={capacity}
                            onChange={(e) =>
                              setCapacity(
                                Math.max(1, Number(e.target.value) || 1),
                              )
                            }
                          />
                        </div>

                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 m-3 text-left w-full">
                          Selected dates: {getSelectedDatesText()}
                        </div>

                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 mx-3 mb-3 text-left w-full">
                          Apartments selected: {selectedListingIds.length}
                        </div>
                      </div>
                    </div>

                    <div className="w-[40%] border-r bg-slate-50/40 pt-3 text-center sm:pt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Marketing Window for Appointment
                      </Dialog.Title>
                      <div>
                        <div className="bg-gray-50 m-2 rounded-lg">
                          <div className="w-full flex items-center justify-center pt-4">
                            <div className="flex items-center bg-gray-100 rounded-full w-full max-w-sm">
                              <button
                                onClick={() => setIsConfigureByDate(true)}
                                className={
                                  "w-1/2 text-xs text-center py-2 rounded-full font-semibold transition duration-200 " +
                                  (isConfigureByDate
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-600")
                                }
                              >
                                Activate by Schedule
                              </button>

                              <button
                                onClick={() => setIsConfigureByDate(false)}
                                className={
                                  "w-1/2 text-xs text-center py-2 rounded-full font-semibold transition duration-200 " +
                                  (!isConfigureByDate
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-600")
                                }
                              >
                                Activate by Lead Time
                              </button>
                            </div>
                          </div>

                          {isConfigureByDate ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <div className="m-2 p-2">
                                <div className="flex flex-row items-center gap-4 m-3">
                                  <div className="w-full text-sm">
                                    <DateTimePicker
                                      label="Opening Time"
                                      value={marketingOpeningTime}
                                      onChange={(date) =>
                                        setMarketingOpeningTime(date)
                                      }
                                      slotProps={{
                                        textField: {
                                          size: "medium",
                                          fullWidth: true,
                                          className: "bg-white rounded-md",
                                        },
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-center">
                                    <span className="text-gray-400">→</span>
                                  </div>
                                  <div className="w-full">
                                    <DateTimePicker
                                      label="Closing Time"
                                      value={marketingClosingTime}
                                      onChange={(date) =>
                                        setMarketingClosingTime(date)
                                      }
                                      slotProps={{
                                        textField: {
                                          size: "medium",
                                          fullWidth: true,
                                          className: "bg-white rounded-md",
                                        },
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </LocalizationProvider>
                          ) : (
                            <div className="flex justify-between bg-gray-50 m-2 gap-2 p-6 rounded-lg">
                              <div className="relative bg-inherit">
                                <input
                                  type="number"
                                  value={openHoursBefore ?? ""}
                                  onChange={(e) =>
                                    setOpenHoursBefore(
                                      e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : undefined,
                                    )
                                  }
                                  className="h-12 w-full rounded text-gray-900 px-2 focus:outline-none"
                                  placeholder="0"
                                />
                                <label className="absolute left-0 -top-2 text-xs text-gray-900 bg-inherit mx-1 px-1">
                                  Hours Before this appt.
                                </label>
                              </div>
                              <div className="relative bg-inherit">
                                <input
                                  type="number"
                                  value={openHoursAfter ?? ""}
                                  onChange={(e) =>
                                    setOpenHoursAfter(
                                      e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : undefined,
                                    )
                                  }
                                  className="h-12 w-full rounded text-gray-900 px-2 focus:outline-none"
                                  placeholder="0"
                                />
                                <label className="absolute left-0 -top-2 text-xs text-gray-900 bg-inherit mx-1 px-1">
                                  Hours After pre appt.
                                </label>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-start justify-between bg-gray-50 m-2 p-6 gap-2 rounded-lg">
                          <div className="flex flex-col items-start gap-2">
                            <div className="flex items-start mb-2">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={resendUnconfirmed}
                                  onChange={() =>
                                    setResendUnconfirmed((prev) => !prev)
                                  }
                                  className="w-4 h-4 border border-gray-300 rounded-sm bg-gray-50 focus:ring-3 focus:ring-blue-300"
                                />
                              </div>
                              <label className="ms-2 text-sm font-medium text-gray-900">
                                Resend to Tenants Who Ignored or Canceled
                              </label>
                            </div>
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={resendCancelled}
                                  onChange={() =>
                                    setResendCancelled((prev) => !prev)
                                  }
                                  className="w-4 h-4 border border-gray-300 rounded-sm bg-gray-50 focus:ring-3 focus:ring-blue-300"
                                />
                              </div>
                              <label className="ms-2 text-sm font-medium text-gray-900">
                                Resend Emails for Appointments We Canceled
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex w-[30%] flex-col items-center justify-start bg-slate-50/30 pt-4">
                      <div className="flex items-center bg-gray-100 rounded-full gap-2">
                        <button
                          className="text-xs text-center p-2 rounded-full font-semibold bg-blue-600 text-white"
                          disabled
                        >
                          Showing reminder
                        </button>
                      </div>

                      <div className="flex w-full max-w-sm justify-between p-4 rounded text-white">
                        <div className="mt-3 flex flex-col text-center sm:mt-5 w-full">
                          <Dialog.Title
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900 mb-3"
                          >
                            Showing Reminder
                          </Dialog.Title>

                          <div className="bg-gray-50 rounded-lg my-2 p-3">
                            <label className="flex items-center justify-start text-gray-600 text-[13px] font-medium">
                              Reminders
                            </label>
                            <div className="mt-2">
                              <div className="flex items-center justify-between border-b rounded-lg gap-4 p-2">
                                <div className="flex flex-wrap w-full items-center justify-between gap-1">
                                  <div className="flex w-full items-center justify-start">
                                    <input
                                      type="number"
                                      value={days !== undefined ? days : ""}
                                      onChange={(e) =>
                                        setDays(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      min={0}
                                      className="text-gray-900 text-center block w-full max-w-[48px] p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0"
                                      placeholder="dd"
                                    />
                                    <span>:</span>
                                    <input
                                      type="number"
                                      value={hours !== undefined ? hours : ""}
                                      onChange={(e) =>
                                        setHours(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      max={23}
                                      min={0}
                                      className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0"
                                      placeholder="hh"
                                    />
                                    <span>:</span>
                                    <input
                                      type="number"
                                      value={
                                        minutes !== undefined ? minutes : ""
                                      }
                                      onChange={(e) =>
                                        setMinutes(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      max={59}
                                      min={0}
                                      className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0"
                                      placeholder="mm"
                                    />
                                  </div>
                                  <button
                                    className="flex w-full items-center justify-end gap-1 text-blue-800 text-xs font-medium active:translate-y-[1px]"
                                    onClick={handleAddReminder}
                                  >
                                    Add New Reminder
                                  </button>
                                </div>
                              </div>

                              <div className="mt-2 flex items-center flex-wrap gap-3">
                                <div className="w-max px-3 py-1.5 border flex items-center justify-center gap-2 rounded-full shadow">
                                  <span className="text-[10px] text-black font-medium">
                                    Sunday before the showing
                                  </span>
                                </div>
                                {relativeReminderMinutes.map((r, i) => (
                                  <div
                                    key={`${r}-${i}`}
                                    className="w-max px-3 py-1.5 border flex items-center justify-center gap-2 rounded-full shadow"
                                  >
                                    <span className="text-[10px] text-black font-medium">
                                      {displayReminder(r)}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteReminder(r)}
                                      className="text-red-600 text-xs font-semibold"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap md:flex-nowrap w-full mt-5 items-center justify-center gap-3">
                    <button
                      type="button"
                      className="flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => setIsCreateSlotModalOpen(false)}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                      onClick={createSlots}
                    >
                      Generate Appointments
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={isDeleteSlotModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          initialFocus={deleteCancelButtonRef}
          onClose={closeDeleteSlotModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Delete slot?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      This will permanently delete the selected slot.
                    </p>
                  </div>

                  <div className="mt-5 sm:mt-6 flex gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      onClick={closeDeleteSlotModal}
                      ref={deleteCancelButtonRef}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500"
                      onClick={confirmDeleteSlot}
                    >
                      Delete Slot
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
